/**
 * 커뮤니티 글의 고민 유형 태그 (2026-10-22, 대표 명세 2·3장). 시드 글 tags 값 전부를 고정 목록으로 삼았다.
 * - 순서가 화면(글쓰기 칩, 피드의 '다른 고민' 칩 줄) 순서다. id 는 한 번 정하면 바꾸지 않는다 (저장된 글이 id 를 들고 있다)
 * - group: make = 제조사 고민 (주간 고민 베스트 기준), menu = 메뉴 속성 고민
 * - 글의 주제다. 메뉴의 속성인 메뉴 꼬리표(menu-tags.ts)와는 다른 칸이다
 * - 메뉴·코스, 판매 전략은 2026-11-01 커뮤니티 탭(COMMUNITY_TABS)과 함께 더했다. 옛 앱(1.0.8 까지)은 모르는 id 라 라벨을 그리지 않는다
 */
export type ConcernTagGroup = 'make' | 'menu'

export const CONCERN_TAGS = [
  {
    id: 'menu-course',
    ko: '메뉴·코스',
    en: 'Menu & courses',
    ja: 'メニュー・コース',
    zh: '菜单与套餐',
    group: 'menu',
  },
  {
    id: 'cost-margin',
    ko: '원가·마진',
    en: 'Cost & margin',
    ja: '原価・利益率',
    zh: '成本与利润',
    group: 'make',
  },
  {
    id: 'factory-moq',
    ko: '공장 견적·MOQ',
    en: 'Factory quotes & MOQ',
    ja: '工場見積・MOQ',
    zh: '工厂报价与起订量',
    group: 'make',
  },
  {
    id: 'packaging-label',
    ko: '패키징·라벨',
    en: 'Packaging & labels',
    ja: 'パッケージ・ラベル',
    zh: '包装与标签',
    group: 'make',
  },
  {
    id: 'sales-strategy',
    ko: '판매 전략',
    en: 'Sales strategy',
    ja: '販売戦略',
    zh: '销售策略',
    group: 'make',
  },
  {
    id: 'sourcing-season',
    ko: '재료 수급·시즌성',
    en: 'Sourcing & seasonality',
    ja: '原材料調達・季節性',
    zh: '原料供应与季节性',
    group: 'make',
  },
  {
    id: 'channel',
    ko: '유통·채널',
    en: 'Distribution & channels',
    ja: '流通・販路',
    zh: '流通与渠道',
    group: 'make',
  },
  {
    id: 'quality-haccp',
    ko: '품질·위생·HACCP',
    en: 'Quality, hygiene & HACCP',
    ja: '品質・衛生・HACCP',
    zh: '质量卫生与HACCP',
    group: 'make',
  },
  {
    id: 'regulation',
    ko: '규제·인증',
    en: 'Regulations & certification',
    ja: '規制・認証',
    zh: '法规与认证',
    group: 'make',
  },
  {
    id: 'dietary',
    ko: '식이 제한',
    en: 'Dietary needs',
    ja: '食事制限',
    zh: '饮食限制',
    group: 'menu',
  },
  {
    id: 'testing',
    ko: '테스트 중',
    en: 'Still testing',
    ja: 'テスト中',
    zh: '测试中',
    group: 'menu',
  },
  {
    id: 'technique',
    ko: '기법 중심',
    en: 'Technique-driven',
    ja: '技法中心',
    zh: '以技法为主',
    group: 'menu',
  },
  {
    id: 'intl-main',
    ko: '국제·서양 메인',
    en: 'International & Western mains',
    ja: '各国・洋食メイン',
    zh: '国际与西式主菜',
    group: 'menu',
  },
] as const satisfies readonly {
  id: string
  ko: string
  en: string
  ja: string
  zh: string
  group: ConcernTagGroup
}[]

export type ConcernTagId = (typeof CONCERN_TAGS)[number]['id']
export const CONCERN_TAG_IDS: readonly ConcernTagId[] = CONCERN_TAGS.map((t) => t.id)

/** 한 글에 고를 수 있는 고민 유형 수 */
export const CONCERN_TAG_MAX = 3

/**
 * 시드·명세 표기에서 id 로. 화면 라벨(ko) 전부와 명세의 다른 표기(공장견적·MOQ, 테스트중).
 * 찾을 때는 공백을 무시한다 (normalizeConcernTags)
 */
export const CONCERN_TAG_ALIASES: Record<string, ConcernTagId> = {
  ...Object.fromEntries(CONCERN_TAGS.map((t) => [t.ko, t.id] as const)),
  '공장견적·MOQ': 'factory-moq',
  테스트중: 'testing',
  // 커뮤니티 탭 라벨 (COMMUNITY_TABS) 이 태그 라벨과 다른 것. '포장' 은 11/01 첫 탭 라벨 (옛 링크·시드 표기)
  포장: 'packaging-label',
  '포장 고민': 'packaging-label',
  '코스 구성': 'menu-course',
}

const isConcernTagId = (value: string): value is ConcernTagId =>
  (CONCERN_TAG_IDS as readonly string[]).includes(value)

const squash = (value: string) => value.replace(/\s+/g, '')
const BY_SQUASHED = new Map<string, ConcernTagId>(
  Object.entries(CONCERN_TAG_ALIASES).map(([alias, id]) => [squash(alias), id]),
)

/** id 또는 별칭을 id 로. 모르는 값은 버리고, 중복 제거, 최대 3개 (앞에서부터) */
export function normalizeConcernTags(ids: readonly string[] | null | undefined): ConcernTagId[] {
  const out: ConcernTagId[] = []
  for (const raw of ids ?? []) {
    if (typeof raw !== 'string') continue
    const value = raw.trim()
    const id = isConcernTagId(value) ? value : BY_SQUASHED.get(squash(value))
    if (id && !out.includes(id)) out.push(id)
    if (out.length === CONCERN_TAG_MAX) break
  }
  return out
}

/** 라벨. 모르는 id 는 id 그대로 돌려준다 */
export function concernTagLabel(id: string, language: string): string {
  const tag = CONCERN_TAGS.find((t) => t.id === id)
  if (!tag) return id
  if (language === 'ko' || language === 'ja' || language === 'zh') return tag[language]
  return tag.en
}

/**
 * 커뮤니티 피드 맨 위의 고민 탭 (2026-11-01 대표 참고 이미지: 전체 고민 / 원가·마진 / 포장 고민 / 코스 구성 / 판매 전략).
 * 탭 하나 = 고민 유형 하나 (all 은 거르지 않음). 서버 GET /community/posts · /community/best 의 concern 값을 그대로 쓴다.
 * 탭에 없는 고민 유형(공장 견적·MOQ, 규제·인증 등)은 필터의 '다른 고민' 칩으로 고른다.
 * 탭 라벨은 고민 유형 라벨과 다를 수 있다 (포장 고민 = 패키징·라벨, 코스 구성 = 메뉴·코스). id 는 바꾸지 않는다
 */
export const COMMUNITY_TABS = [
  { id: 'all', concern: null, ko: '전체 고민', en: 'All', ja: 'すべての相談', zh: '全部问题' },
  {
    id: 'cost-margin',
    concern: 'cost-margin',
    ko: '원가·마진',
    en: 'Cost & margin',
    ja: '原価・利益率',
    zh: '成本与利润',
  },
  {
    id: 'packaging',
    concern: 'packaging-label',
    ko: '포장 고민',
    en: 'Packaging',
    ja: '包装の相談',
    zh: '包装问题',
  },
  {
    id: 'menu-course',
    concern: 'menu-course',
    ko: '코스 구성',
    en: 'Course planning',
    ja: 'コース構成',
    zh: '套餐搭配',
  },
  {
    id: 'sales-strategy',
    concern: 'sales-strategy',
    ko: '판매 전략',
    en: 'Sales strategy',
    ja: '販売戦略',
    zh: '销售策略',
  },
] as const satisfies readonly {
  id: string
  concern: ConcernTagId | null
  ko: string
  en: string
  ja: string
  zh: string
}[]

export type CommunityTabId = (typeof COMMUNITY_TABS)[number]['id']

/** 탭에 올라간 고민 유형 (탭 아래 '다른 고민' 칩 줄은 이것을 뺀 나머지) */
export const COMMUNITY_TAB_CONCERNS: readonly ConcernTagId[] = COMMUNITY_TABS.flatMap((t) =>
  t.concern ? [t.concern] : [],
)
